import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import subjectAndGroupingApis from '../../../apis/subjects';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../../enums';
import {
  SubjectsButton,
  CloneNoteText,
  CloneSectionTitle,
  CloneFormContainer
} from '../styled';
import { CloneSubjectsModalProps } from '../types';

const CloneSubjectsModal: React.FC<CloneSubjectsModalProps> = ({
  open,
  onClose,
  grade,
  division,
  allClasses,
  school,
  onSubjectsCloned,
  showToast
}) => {
  const [cloneGrade, setCloneGrade] = useState('');
  const [cloneDivision, setCloneDivision] = useState('');
  const [cloneGradeOptions, setCloneGradeOptions] = useState<string[]>([]);
  const [cloneDivisionOptions, setCloneDivisionOptions] = useState<string[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setCloneGrade('');
      setCloneDivision('');
      setCloneDivisionOptions([]);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (allClasses) {
      setCloneGradeOptions(allClasses.grades || []);
    }
  }, [allClasses]);

  useEffect(() => {
    if (allClasses && cloneGrade) {
      const divisions = allClasses.classes
        .filter((schoolClass) => schoolClass.grade === cloneGrade)
        .map((schoolClass) => schoolClass.division);
      setCloneDivisionOptions(divisions);
    } else if (allClasses) {
      setCloneDivisionOptions(allClasses.divisions || []);
    } else {
      setCloneDivisionOptions([]);
    }
  }, [allClasses, cloneGrade]);

  const handleClone = async () => {
    setIsLoading(true);
    try {
      await subjectAndGroupingApis.cloneSubjects(school, {
        cloneFrom: {
          grade: cloneGrade,
          division: cloneDivision
        },
        cloneTo: {
          grade,
          division
        }
      });
      onSubjectsCloned();
      onClose();
      showToast('Subjects cloned successfully!', SnackbarSeverityType.success);
    } catch (error: any) {
      console.error('Error cloning subjects:', error);
      const errorMessage =
        error?.errors?.[0]?.message || 'Failed to clone subjects.';
      showToast(errorMessage, SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isCloneButtonDisabled =
    !school || !grade || !division || !cloneGrade || !cloneDivision;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Clone Subjects'
      actions={
        <>
          <Button onClick={onClose} color='primary' variant='contained'>
            Cancel
          </Button>
          <SubjectsButton
            onClick={handleClone}
            disabled={isLoading || isCloneButtonDisabled}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Clone'
            )}
          </SubjectsButton>
        </>
      }
    >
      <CloneNoteText variant='body2'>
        Note: You are cloning subjects for{' '}
        <strong>
          {grade}-{division}
        </strong>
      </CloneNoteText>
      <CloneSectionTitle variant='h6'>Clone from</CloneSectionTitle>
      <CloneFormContainer>
        <DropdownSelect
          label='Grade'
          placeholder='Select Grade'
          name='cloneGrade'
          value={cloneGrade}
          options={cloneGradeOptions.map((grade) => ({
            value: grade,
            label: grade
          }))}
          onChange={(e) => {
            setCloneGrade(e.target.value as string);
            setCloneDivision('');
          }}
          loading={cloneGradeOptions.length === 0 && !!allClasses}
        />
        <DropdownSelect
          label='Division'
          placeholder='Select Division'
          name='cloneDivision'
          value={cloneDivision}
          options={cloneDivisionOptions.map((division) => ({
            value: division,
            label: division
          }))}
          onChange={(e) => setCloneDivision(e.target.value as string)}
          loading={cloneDivisionOptions.length === 0 && !!cloneGrade}
        />
      </CloneFormContainer>
    </DialogModal>
  );
};

export default CloneSubjectsModal;
